import React, { useState, useEffect } from "react";
import Layout from "../../components/layout/Layout";
import { APIURL } from "../../redux/action/config";
import { useParams } from "react-router-dom";
import Logo from "../../assets/images/logo.png";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "../invoice/style.css";
import dateFormat from "dateformat";
import { formatPhoneNumber } from "../../utils/phone";
import { filterActiveMemberships } from "../../utils/filter-active-membership";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 12, fontFamily: "Helvetica" },
  headerTop: {
    padding: 10,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  headerTitle: {
    backgroundColor: "#ff4e00",
    padding: 10,
    color: "white",
    textAlign: "center",
  },
  logo: { width: 150 },
  titleText: { color: "white", fontSize: 18, fontWeight: "bold" },
  dateText: { color: "white", fontSize: 10, marginTop: 5 },
  section: { marginVertical: 10 },
  heading: { fontSize: 14, fontWeight: "bold", marginBottom: 5, color: "#333" },
  text: { marginBottom: 3, fontSize: 11 },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginTop: 10,
    marginBottom: 15,
  },
  tableRow: { flexDirection: "row" },
  th: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#f2f2f2",
    padding: 5,
    fontWeight: "bold",
    fontSize: 10,
  },
  td: {
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    fontSize: 10,
  },
  colMembership: { width: "26%" },
  colDate: { width: "20%" },
  colAmount: { width: "16%" },
  colStatus: { width: "20%" },
  colRef: { width: "18%" },
  statusRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
    marginBottom: 15,
  },
  statusLabel: { fontSize: 11, color: "#333", marginRight: 6 },
  statusValue: { fontSize: 13, fontWeight: "bold" },
});

const STATUS_COLORS = {
  Active: "#1a7d3c",
  Expired: "#6c757d",
  "Payment Failed": "#c0392b",
  Inactive: "#b8860b",
};

function normalizeTransition(t) {
  const rawDate =
    t?.at || t?.transactionDate || (t?.created ? new Date(t.created * 1000) : null);
  const amount =
    typeof t?.amount === "number"
      ? (t.amount / 100).toFixed(2)
      : typeof t?.amount_received === "number"
        ? (t.amount_received / 100).toFixed(2)
        : null;

  return {
    date: rawDate ? dateFormat(new Date(rawDate), "UTC:d mmm yyyy") : "N/A",
    amount: amount ? `$${amount}` : "N/A",
    status: t?.status || "unknown",
    reference: t?.reference || t?.invoiceId || t?.paymentIntentId || t?.id || "N/A",
  };
}

const RecordDocument = ({ customer, rows, membershipStatus }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.headerTop}>
        <Image src={Logo} style={styles.logo} />
      </View>
      <View style={styles.headerTitle}>
        <Text style={styles.titleText}>Membership Payment Record</Text>
        <Text style={styles.dateText}>
          Generated: {dateFormat(new Date(), "d mmmm yyyy")}
        </Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.heading}>Customer Details</Text>
        <Text style={styles.text}>
          {customer?.firstName} {customer?.lastName}
        </Text>
        <Text style={styles.text}>{customer?.email}</Text>
        <Text style={styles.text}>
          {customer?.phone ? formatPhoneNumber(customer.phone) : "N/A"}
        </Text>
        <Text style={styles.text}>
          Joined:{" "}
          {customer?.joi_date
            ? dateFormat(customer.joi_date, "UTC:d mmmm yyyy")
            : "N/A"}
        </Text>
      </View>

      <View style={styles.statusRow}>
        <Text style={styles.statusLabel}>Current Membership Status:</Text>
        <Text
          style={[
            styles.statusValue,
            { color: STATUS_COLORS[membershipStatus] || "#333" },
          ]}
        >
          {membershipStatus}
        </Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.heading}>Payment History</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={[styles.th, styles.colMembership]}>
              <Text>Membership</Text>
            </View>
            <View style={[styles.th, styles.colDate]}>
              <Text>Date</Text>
            </View>
            <View style={[styles.th, styles.colAmount]}>
              <Text>Amount</Text>
            </View>
            <View style={[styles.th, styles.colStatus]}>
              <Text>Status</Text>
            </View>
            <View style={[styles.th, styles.colRef]}>
              <Text>Reference</Text>
            </View>
          </View>
          {rows.length === 0 ? (
            <View style={styles.tableRow}>
              <View style={[styles.td, { width: "100%" }]}>
                <Text>No payment records found.</Text>
              </View>
            </View>
          ) : (
            rows.map((r, i) => (
              <View style={styles.tableRow} key={i}>
                <View style={[styles.td, styles.colMembership]}>
                  <Text>{r.membershipName}</Text>
                </View>
                <View style={[styles.td, styles.colDate]}>
                  <Text>{r.date}</Text>
                </View>
                <View style={[styles.td, styles.colAmount]}>
                  <Text>{r.amount}</Text>
                </View>
                <View style={[styles.td, styles.colStatus]}>
                  <Text>{r.status}</Text>
                </View>
                <View style={[styles.td, styles.colRef]}>
                  <Text>{r.reference}</Text>
                </View>
              </View>
            ))
          )}
        </View>
      </View>
    </Page>
  </Document>
);

const ViewCustomerRecord = () => {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const getCustomer = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(`${APIURL}/api/v1/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomer(res?.data?.data?.user);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to load customer record"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const memberships = customer?.purched_memberships || [];
  const { status: membershipStatus } = filterActiveMemberships(
    memberships,
    customer?.subscriptionType,
    customer?.subscriptionId
  );

  const rows = memberships.flatMap((m) =>
    (Array.isArray(m.transition) ? m.transition : []).map((t) => ({
      membershipName: m.name || "N/A",
      ...normalizeTransition(t),
    }))
  );

  const printPage = () => {
    document.body.classList.add("print-content-only");
    window.print();
    document.body.classList.remove("print-content-only");
  };

  return (
    <React.Fragment>
      <ToastContainer autoClose={2000} />
      <Layout title="Customer Payment Record" page="customers">
        <div
          className="content d-flex flex-column flex-column-fluid"
          id="kt_content"
        >
          <div id="kt_content_container" className="container-fluid">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "60vh" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="content_container">
                <div className="pay-section">
                  <PDFDownloadLink
                    document={
                      <RecordDocument
                        customer={customer}
                        rows={rows}
                        membershipStatus={membershipStatus}
                      />
                    }
                    fileName={`${customer?.firstName || "customer"}-membership-record.pdf`}
                  >
                    {({ loading: pdfLoading }) => (
                      <button>
                        {pdfLoading ? "Preparing PDF..." : "Download PDF"}
                      </button>
                    )}
                  </PDFDownloadLink>
                  <button onClick={printPage} className="btn btn-primary ms-2">
                    Print
                  </button>
                </div>
                <div className="invoice-container">
                  <div className="header-new">
                    <img src={Logo} width={200} alt="Logo" />
                  </div>
                  <div className="header">
                    <h2>Membership Payment Record</h2>
                    <span
                      style={{
                        textAlign: "center",
                        color: "white",
                        fontSize: "14",
                      }}
                    >
                      Generated: {dateFormat(new Date(), "d mmmm yyyy")}
                    </span>
                  </div>
                  <div className="content-section">
                    <div className="billing-address">
                      <h3>Customer Details</h3>
                      <p>
                        {customer?.firstName} {customer?.lastName}
                      </p>
                      <p>{customer?.email}</p>
                      <p>
                        {customer?.phone
                          ? formatPhoneNumber(customer.phone)
                          : "N/A"}
                      </p>
                      <p>
                        Joined:{" "}
                        {customer?.joi_date
                          ? dateFormat(customer.joi_date, "UTC:d mmmm yyyy")
                          : "N/A"}
                      </p>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        margin: "12px 0 20px",
                      }}
                    >
                      <span style={{ fontSize: "13px", color: "#333" }}>
                        Current Membership Status:
                      </span>
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: STATUS_COLORS[membershipStatus] || "#333",
                        }}
                      >
                        {membershipStatus}
                      </span>
                    </div>
                    <h3 style={{ marginTop: "10px" }}>Payment History</h3>
                    <table className="bordered-table">
                      <thead>
                        <tr>
                          <th>Membership</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Reference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.length === 0 ? (
                          <tr>
                            <td colSpan={5}>No payment records found.</td>
                          </tr>
                        ) : (
                          rows.map((r, i) => (
                            <tr key={i}>
                              <td>{r.membershipName}</td>
                              <td>{r.date}</td>
                              <td>{r.amount}</td>
                              <td className="text-capitalize">{r.status}</td>
                              <td>{r.reference}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Layout>
    </React.Fragment>
  );
};

export default ViewCustomerRecord;
